import {Component, inject} from '@angular/core';
import {HttpClient} from '@angular/common/http';
import {
  AbstractControlOptions,
  UntypedFormBuilder,
  UntypedFormGroup,
  Validators,
  FormsModule,
  ReactiveFormsModule
} from '@angular/forms';
import {Router} from '@angular/router';
import {ToastrService} from 'ngx-toastr';
import Utils from '../utils/utils';
import {environment} from '../../environments/environment';
import {ILogin, Login} from '../../shared/model/login.model';
import {MatCard, MatCardTitle} from '@angular/material/card';
import {MatFormField, MatLabel, MatError} from '@angular/material/form-field';
import {MatInput} from '@angular/material/input';
import {MatIcon} from '@angular/material/icon';
import {MatButton} from '@angular/material/button';
import {MatCheckbox} from '@angular/material/checkbox';
import {MatProgressSpinner} from '@angular/material/progress-spinner';


@Component({
  selector: 'app-signup',
  templateUrl: './signup.component.html',
  styleUrls: ['./signup.component.css'],
  imports: [MatCard, MatCardTitle, FormsModule, ReactiveFormsModule, MatFormField, MatLabel, MatInput, MatError, MatIcon, MatButton, MatCheckbox, MatProgressSpinner]
})
export class SignupComponent {
  private readonly http = inject(HttpClient);
  private readonly fb = inject(UntypedFormBuilder);
  private readonly router = inject(Router);
  private readonly toastr = inject(ToastrService);


  email = '';
  form: UntypedFormGroup = this.fb.group({
    email: [this.email, [Validators.required, Validators.email]],
    password: ['', [Validators.required, Validators.minLength(8)]],
    passwordConfirm: ['', Validators.required],
    confirmationCode: ['', [Validators.required, Validators.minLength(6), Validators.maxLength(6)]],
    validCgu: [false, Validators.requiredTrue]
  }, {
    validator: Utils.passwordMatchValidator
  } as AbstractControlOptions);
  error = '';
  nextStepSignup = false;
  isLoading = false;
  apiUrl = environment.apiUrl;
  showPassword = false;
  showPasswordRepeat = false;
  login: ILogin = new Login();

  showSuccess(message: string): void {
    this.toastr.success(message, 'OK', {
      timeOut: 3000
    });
  }

  showError(error: string): void {
    this.toastr.error(error, 'Erreur', {
      timeOut: 3000
    });
  }

  signup(): void {
    this.isLoading = true;
    this.http.post(this.apiUrl + '/auth/signup', this.login, {observe: 'response', responseType: 'text'})
      .subscribe({
        next: () => {
          this.isLoading = false;
          this.nextStepSignup = true;
        },
        error: (error: string) => {
          this.isLoading = false;
          switch (error) {
            case 'ERR_MAIL_NOT_VALID':
              this.error = 'E-mail non valide';
              break;

            case 'ERR_MAIL_TAKEN':
              this.error = 'Compte déjà existant';
              break;

            case 'ERR_CUSTOMER_NOT_FOUND':
              this.error = 'Aucun compte client InExtenso n\'a été trouvé avec cet email';
              break;

            default:
              this.error = 'Merci de réessayer dans quelques instants';
              break;
          }
          this.showError(this.error);
        }
      });
  }

  signupValidation(): void {
    this.isLoading = true;
    this.http.post(this.apiUrl + '/auth/signup-validation', this.login, {observe: 'response', responseType: 'text'})
      .subscribe({
        next: () => {
          this.router.navigate(['login']).then();
          this.isLoading = false;
          this.showSuccess('Votre compte a bien été créé et activé. Vous pouvez désormais vous connecter');

          if (localStorage.getItem('ieo_cb_reference') != null && localStorage.getItem('ieo_cb_amount') != null) {
            const body = {
              email: this.email,
              reference: localStorage.getItem('ieo_cb_reference'),
              amount: localStorage.getItem('ieo_cb_amount')
            };
            this.http.post(this.apiUrl + '/checkout/send-recap-mail', body, {observe: 'response', responseType: 'text'})
              .subscribe(() => {
                localStorage.removeItem('ieo_cb_reference');
                localStorage.removeItem('ieo_cb_amount');
              });
          } else {
            localStorage.removeItem("ieo_cb_reference");
            localStorage.removeItem("ieo_cb_amount");
          }
        },
        error: (error: string) => {
          switch (error) {
            case 'ERR_ALREADY_ACTIVATED':
              this.error = 'Compte déjà activé';
              break;

            case 'ERR_BAD_ACTIVATION_CODE':
              this.error = 'Code de confirmation invalide';
              break;

            case 'ERR_ACC_NOT_EXIST':
              this.error = 'Le compte n\'existe pas';
              break;

            default:
              this.error = 'Merci de réessayer dans quelques instants';
              break;
          }
          this.showError(this.error);
          this.isLoading = false;
        }
      });
  }

  public resendConfirmationCode(): void {
    this.isLoading = true;
    this.login.typeMail = 'signup';
    this.login.password = null;
    this.http.post(this.apiUrl + '/auth/resend-code', this.login, {observe: 'response', responseType: 'text'})
      .subscribe({
        next: () => {
          this.showSuccess('Un nouveau code vient de vous être envoyé sur votre adresse email');
          this.isLoading = false;
        },
        error: (error: string) => {
          switch (error) {
            case 'ERR_MAIL_NOT_RESENT':
              this.error = 'Impossible de renvoyer le mail de confirmation, merci de réessayer dans quelques minutes';
              break;

            case 'ERR_USER_NOT_FOUND':
              this.error = 'L\'utilisateur n\'existe pas';
              break;

            default:
              this.error = 'Merci de réessayer dans quelques instants';
              break;
          }
          this.showError(this.error);
          this.isLoading = false;
        }
      });
  }

  public openCgu(): void {
    this.router.navigate(['cgu']).then();
  }

  clickShowPassword(): void {
    this.showPassword = !this.showPassword;
  }

  clickShowPasswordRepeat(): void {
    this.showPasswordRepeat = !this.showPasswordRepeat;
  }
}
